/**
 * The getting-set-up card, on the dashboard until it has nothing to say.
 *
 * Owner-only, because every step it checks is an owner act; hidden the
 * moment every step is done, and dismissible for the owner who prefers to
 * find their own way — that choice sticks per browser, which is exactly
 * the durability a "stop showing me this" deserves.
 */

import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlural, useT } from '@/lib/i18n';
import { Button, Card, CardHeader, Meter } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { onboardingDone, onboardingSteps } from '@/lib/onboarding';
import { useAuthStore } from '@/lib/store';
import { cn } from '@/lib/utils';

const DISMISS_KEY = 'mc-getting-started-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function GettingStartedCard() {
  const plural = usePlural();
  const t = useT();
  const user = useAuthStore((state) => state.user);
  const [dismissed, setDismissed] = useState(readDismissed);
  const owner = user?.role === 'owner';

  const system = useQuery({
    queryKey: ['system'],
    queryFn: () => api.system(),
    enabled: owner && !dismissed,
  });
  const workspaces = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces(),
    enabled: owner && !dismissed,
  });
  const runs = useQuery({
    queryKey: ['runs', 'recent'],
    queryFn: () => api.runs({ limit: 1 }),
    enabled: owner && !dismissed,
  });
  const push = useQuery({
    queryKey: ['push-status'],
    queryFn: () => api.push.status(),
    enabled: owner && !dismissed,
  });
  const updater = useQuery({
    queryKey: ['update-apply'],
    queryFn: () => api.updateApplyStatus(),
    enabled: owner && !dismissed,
  });

  if (!owner || dismissed) return null;
  // Say nothing until the answers are in: a checklist that flashes
  // half-done while queries settle teaches people to ignore it.
  if (!system.data || !workspaces.data || !runs.data || !push.data || !updater.data) return null;

  const steps = onboardingSteps({
    authenticated: system.data.claudeCli.authenticated,
    workspaces: workspaces.data.workspaces.length,
    hasRuns: runs.data.runs.length > 0,
    totpEnabled: user?.totpEnabled ?? false,
    pushDevices: push.data.devices,
    updaterAvailable: updater.data.available,
  });
  if (onboardingDone(steps)) return null;

  const remaining = steps.filter((step) => !step.done).length;
  const done = steps.length - remaining;

  const dismiss = (): void => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // A browser refusing storage still gets the session-long dismissal.
    }
    setDismissed(true);
  };

  return (
    <Card>
      <CardHeader
        title={t('Getting set up')}
        description={plural(
          remaining,
          '{n} step left before everything this can do is switched on.',
          '{n} steps left before everything this can do is switched on.',
        )}
        actions={
          <Button variant="ghost" size="icon" aria-label={t(
            'Dismiss the checklist',
          )} onClick={dismiss}>
            <X className="size-4" />
          </Button>
        }
      />
      <ul className="space-y-1 px-4 pb-4">
        {/*
          * What is done becomes a bar, not three struck-through rows.
          *
          * Measured on the dashboard: six rows filled 343px at the top of the
          * screen, and half of them said nothing an operator can act on — a
          * finished step is finished. Keeping the count and the bar keeps the
          * sense of progress, which is the only thing those rows carried, and
          * gives back about ninety pixels of the first screen. `Meter` is the
          * app's existing bar; a second one drawn here would drift from it.
          */}
        {done > 0 ? (
          <li className="flex items-center gap-2.5 px-2 pb-1.5">
            <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden />
            <span className="shrink-0 text-caption text-muted">
              {t('{done} of {total} done', { done, total: steps.length })}
            </span>
            <Meter
              value={done / steps.length}
              tone="success"
              label={t('{done} of {total} done', { done, total: steps.length })}
              className="ml-1 flex-1"
            />
          </li>
        ) : null}
        {steps
          .filter((step) => !step.done)
          .map((step) => {
          /*
           * A checklist explains the step you are on.
           *
           * Six steps carrying two lines each filled four hundred and forty
           * pixels of a phone, in front of everything the dashboard exists to
           * show. The detail of a step you have not reached is not urgent.
           *
           * The trigger used everywhere else cannot go here: the row is a
           * `<Link>`, and a button inside a link is invalid and unreachable by
           * keyboard. `.help-comfortable` says the same thing with no control.
           */
          const next = step.key === steps.find((entry) => !entry.done)?.key;
          return (
          <li key={step.key}>
            <Link
              to={step.href}
              className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-sunken"
            >
              {step.done ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
              ) : (
                <Circle className="mt-0.5 size-4 shrink-0 text-subtle" aria-hidden />
              )}
              <span className="min-w-0">
                <span
                  className={
                    step.done ? 'text-body text-muted line-through' : 'text-body font-medium text-ink'
                  }
                >
                  {t(step.label)}
                </span>
                {!step.done ? (
                  <span
                    className={cn(
                      'text-caption leading-relaxed text-muted',
                      // `block` and `help-comfortable` cannot share an element:
                      // Tailwind's utilities live in a *later* cascade layer,
                      // and a later layer beats any specificity — so the class
                      // was present, the density was compact, and the prose
                      // showed anyway. `help-comfortable` supplies the display
                      // it needs in the density that shows it.
                      next ? 'block' : 'help-comfortable',
                    )}
                  >
                    {t(step.detail)}
                  </span>
                ) : null}
              </span>
            </Link>
          </li>
          );
        })}
      </ul>
    </Card>
  );
}
